import type { PaymentPlan } from '@ctct/domain-layer';
import { envs } from '@ctct/infrastructure-layer';
import type { NonProfitPageStore } from '@ctct/state-client-layer';
import type { UpdatePaymentPlanMutation } from '@ctct/state-server-layer';

import type { TEventSource } from '../../../helpers';
import type { TranslationGetter } from '../../../types';
import { nonProfitEventBus } from '../NonProfitEventBus';
import type { Flags, NonProfitUrlAdapter } from '../types';

export const NON_PROFIT_PAYMENT_PLAN_ID = 3;

export class NonProfitService {
  constructor(
    private pageStore: NonProfitPageStore,
    private paymentPlan: PaymentPlan,
    private updatePaymentPlanMutation: UpdatePaymentPlanMutation,
    private urlAdapter: NonProfitUrlAdapter,
    private flags: Flags,
    private t: TranslationGetter
  ) {
    nonProfitEventBus.emit('nonProfitPaymentPlanGroupIdChanged', paymentPlan.getPaymentPlanGroupId(), {
      source: 'asyncResponse',
    });
    nonProfitEventBus.emit('nonProfitPaymentPlanDiscountsChanged', paymentPlan.getPlanDiscounts(), {
      source: 'asyncResponse',
    });
  }

  public api() {
    return {
      getPaymentPlanGroupId: () => this.paymentPlan.getPaymentPlanGroupId(),
      getIsNonProfit: this.isNonProfit.bind(this),
      disable: () => this.pageStore.setState({ nonProfitDisabled: true }),
      enable: () => this.pageStore.setState({ nonProfitDisabled: false }),
    };
  }

  public data() {
    return {
      getIsNonProfit: this.isNonProfit.bind(this),
      getLabels: () => {
        return {
          nonprofitLabel: this.t('application-layer.pricing-configurator.non-profit-label'),
          nonprofitTooltip: this.t('application-layer.pricing-configurator.non-profit-tooltip'),
        };
      },
    };
  }

  public handlers() {
    return {
      onNonprofitChange: (isNonProfit: boolean) => this.handleNonprofitChange(isNonProfit, 'user'),
    };
  }

  public onMount() {
    const [skipInitializationFromUrl, isCoreMutationLockedAccount, isCoreMutationLockPending] = this.flags;

    const { nonprofit: isNonProfitFromUrl, deleteNonprofit } = this.urlAdapter.getNonProfitParams();

    this.pageStore.setState({ nonProfitInitialPaymentPlanGroupId: this.paymentPlan.getPaymentPlanGroupId() });

    if (isNonProfitFromUrl && !skipInitializationFromUrl && !isCoreMutationLockedAccount) {
      this.handleNonprofitChange(isNonProfitFromUrl === 'true', 'onMount');
    }

    if (!isCoreMutationLockPending && envs.isSecure()) deleteNonprofit();
  }

  private isNonProfit() {
    return this.paymentPlan.getIsNonProfit();
  }

  private async handleNonprofitChange(isNonProfit: boolean, source: TEventSource) {
    const [, isCoreMutationLockedAccount] = this.flags;

    if (isCoreMutationLockedAccount && source !== 'asyncResponse') {
      return;
    }

    if (this.isNonProfit() === isNonProfit) {
      return;
    }

    const { nonProfitInitialPaymentPlanGroupId: initialPaymentPlanGroupId } = this.pageStore.getState();

    // turing off nonprofit  =  go back to the initialPaymentPlanGroupId, *unless* it was the NON_PROFIT_PAYMENT_PLAN_ID
    // in that case set it to null.  This is not ideal, as they may have come from a payment plan id of 52,
    // for example, but we just don't know that, and this is the best we can do.

    const standardPaymentPlanId =
      initialPaymentPlanGroupId === NON_PROFIT_PAYMENT_PLAN_ID ? null : initialPaymentPlanGroupId;

    try {
      this.pageStore.setState({ nonProfitDisabled: true });
      nonProfitEventBus.emit('nonProfitIsChanging', true, { source });

      const paymentPlanGroupId = isNonProfit ? NON_PROFIT_PAYMENT_PLAN_ID : standardPaymentPlanId;

      const result = await this.updatePaymentPlanMutation.mutateAsync({
        paymentPlanGroupId,
      });

      if (result.status === 200) {
        nonProfitEventBus.emit('nonProfitChanged', isNonProfit, { source });
      }
    } catch {
      // should we show error in UI?
    } finally {
      this.pageStore.setState({ nonProfitDisabled: false });
      nonProfitEventBus.emit('nonProfitIsChanging', false, { source });
    }
  }
}
